import React, { ReactElement, ReactNode, Suspense, useContext, useState } from 'react';
import { DocumentNode, useMutation } from '@apollo/client';
import { Mutation } from '@apollo/react-components';
import WorkspaceContext from '../Context/WorkspaceContext';
import AppContext from '../Context/AppContext';
import { useHistory } from 'react-router';
import { checkAction } from '../../utils/OperationPermissions';
import { ActionButton, Button } from '../connectedForm/Button/Button';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import ActionButtonContext from '../Context/ActionButtonContext';
import { HiddenVerticalScrollMenu, ItemRenderer } from '../connectedForm/ActionButtons';
import { InfoTheme } from '../../styles/themes/default/defaultFluentTheme';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react';
import { useDispatch } from 'react-redux';
import ServiceActions from '../../state/modules/service/actions';
import { serviceActions } from '../../state/modules/service';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import * as entities from '../../graphql/entities';
import { IJobMeta, useBackgroundProcess } from '../../utils/BackgroundJobs';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { MenuItem } from 'react-contextmenu';
import {
  IAbstractWorkspaceAction,
  IDeprecatedWorkspaceAction,
  IWorkspaceAction,
  IWorkspaceProps,
} from '../../@types/workspaceTypes';
import { IDeprecatedFormAction, IFormAction } from '../../@types/entityTypes';
import { useActionVariables } from '../../utils/useActionProps';
import { WorkspaceActionHeader, workspaceActionHeaderForEach } from '../ActionButtons/ActionHeader';
import { AlertType } from '../../graphql/schema/p4types';

import ViewContext from '../Context/ViewContext';
import useWorkspaceGenericReportAction from '../../actions/useDynamicReportAction';
import ConfirmBulkActionModal from '../modals/ConfirmBulkActionModal';
import UserContext from '../Context/UserContext';
import { IWorkspaceSelectionState } from './useWorkspaceSelection';
import { Dispatch } from 'redux';
import type { OperationVariables } from '@apollo/client/core';

//Extract the logic for managing the click to a hook, so we can use it in different types of button (e.g. 'top right action button' and 'context button (row right click)').
export const useWorkspaceAction = <TData, TVariables>({
  mutation,
  errorMessage = 'Could not complete action',
  title = 'Processing',
  variables,
  onComplete,
  onFailed,
}: {
  mutation: DocumentNode;
  errorMessage?: string;
  title?: string;
  variables: TVariables;
  onComplete?: (job: IJobMeta) => void;
  onFailed?: () => void;
}) => {
  const workspace = useContext(WorkspaceContext);
  const dispatch = useDispatch();

  const [basicWorkspaceMutation] = useMutation<TData, TVariables>(mutation, {
    onError: (data) => {
      handleComponentQueryError(data, { dispatch, entity: entities.SalesOrder, name: errorMessage });
      onFailed?.();
    },
  });

  const runBackgroundProcess = useBackgroundProcess({
    title: title,
    onComplete: (job: IJobMeta) => {
      workspace.reloadData?.();
      onComplete?.(job);
    },
    onFailed: onFailed,
  });

  return () => {
    basicWorkspaceMutation({
      variables,
    }).then((result) => {
      if (!result) {
        //This error should never be thrown, just here to ensure we get an alert not an error if something did go wrong.
        dispatch(
          serviceActions.genericAlert('Unable to process job', 'No job was returned from the action', AlertType.Error)
        );
        return;
      }
      const key = Object.keys(result.data)[0];
      runBackgroundProcess(result.data[key].result);
    });
  };
};

export const BasicWorkspaceContextAction = ({
  mutation,
  type = 'salesOrders',
  errorMessage,
  title,
  label = 'Action',
  rowData,
}: {
  mutation: DocumentNode;
  type: string;
  errorMessage: string;
  title: string;
  label: string;
  rowData: any;
}) => {
  //Since we expect this to be using one of the 'multi-select' mutations from the top-right workspace actions, build up similar variables (Just specifying the selected row).
  const variables = {
    input: {
      clientMutationId: getClientMutationId(),
      filters: [], //No filters because we're doing positive inclusion and passing the entity directly.
      [`${type}`]: rowData ? [normalizeVariables(rowData)] : [],
      include: true,
    },
  };

  const onClick = useWorkspaceAction({ mutation, errorMessage, title, variables });

  return (
    <MenuItem
      key={'contextActionButtonBreakdown'}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {label}
    </MenuItem>
  );
};

type IBasicWorkspaceActionProps<ActionType = string> = {
  mutation: DocumentNode;
  type?: string;
  errorMessage?: string;
  text: string;
  secondaryText?: string;
  condition?: () => boolean;
  actionReference: ActionType;
  header?: WorkspaceActionHeader;
  onComplete?: (job: IJobMeta) => void;
  onFailed?: () => void;
  additionalNormalizeFields?: Array<string>;
  requiresActionCriteria?: boolean;
};

export const useBasicWorkspaceAction = <ActionType,>({
  mutation,
  type = 'salesOrders',
  errorMessage,
  text = 'Action',
  condition,
  actionReference,
  header = WorkspaceActionHeader.GENERAL,
  onComplete,
  onFailed,
  additionalNormalizeFields,
  requiresActionCriteria = false,
}: IBasicWorkspaceActionProps<ActionType>) => {
  let variables: any = useActionVariables(type, undefined, additionalNormalizeFields);

  if (requiresActionCriteria) {
    variables = {
      input: {
        clientMutationId: variables.input.clientMutationId,
        actionCriteria: {
          filters: variables.input.filters,
          include: variables.input.include,
          actionEntities: variables.input[type],
        },
      },
    };
  }

  const onClick = useWorkspaceAction({ mutation, errorMessage, variables, onComplete, onFailed });
  const basicWorkspaceAction: IWorkspaceAction<ActionType> = {
    key: `action_${text}`,
    text: text,
    onClick: onClick,
    condition,
    actionReference,
    header,
  };
  return basicWorkspaceAction;
};

export function isWorkspaceActionType(
  actionButton: IFormAction<any> | IDeprecatedFormAction<any> | IWorkspaceAction | IDeprecatedWorkspaceAction
): actionButton is IWorkspaceAction {
  return (actionButton as IWorkspaceAction).text !== undefined;
}

export function isDeprecatedWorkspaceActionType(
  actionButton: IFormAction<any> | IDeprecatedFormAction<any> | IWorkspaceAction | IDeprecatedWorkspaceAction
): actionButton is IDeprecatedWorkspaceAction {
  const deprecatedActionButton = actionButton as IDeprecatedWorkspaceAction;
  return !!(
    (deprecatedActionButton.mutationWrapper && deprecatedActionButton.mutation && deprecatedActionButton.call) ||
    deprecatedActionButton.call ||
    deprecatedActionButton.dispatch ||
    deprecatedActionButton.url ||
    deprecatedActionButton.element
  );
}

export type IWorkspaceActionButtonsProps = {
  workspaceProps: IWorkspaceProps<any>;
  actionButtons: Array<IWorkspaceAction | IDeprecatedWorkspaceAction>;
  reportButtons?: Array<any>;
  handleActionOption: (actionProps?: any) => void;
  actionEntities: Array<any>;
  styles: { btnDropdownOption: string };
  noReport: boolean;
};

// noinspection JSUnfilteredForInLoop
/**
 * Workspace Action Buttons component - Can be used in a HOC and provided as a 'workspaceActionButtonsHOC' prop to Workspace for proper nesting.
 * @param workspaceProps - Props from the workspace - Would like to DEPRECATE this eventually. (Since this should be nested properly, should be able to access any required values through context).
 * @param actionButtons - Array of actionButtons, ideally in the form {key, text, onClick} per fluentUI button spec, for display in the actions 'dropdown'
 * @param reportButtons - Array of actionButton, specifically for reporting (E.g. 'Print Labels, Print Delivery Notes, etc). MUST be in the {key, text, onClick} form, other types unsupported for report buttons.
 * @param handleActionOption - An onClick handler for older versions of buttons - New buttons should be providing their own onClick.
 * @param actionEntities -  Workspace currently selected entities.
 * @param genericReportAction -Generic report action definition for the workspace.
 * @param styles -       TODO - Deprecate (Can access the styles directly, doesn't need to be a prop).
 * @returns {JSX.Element} A JSX element with all the workspace action buttons rendered in it.
 */
export const WorkspaceActionButtons = ({
  workspaceProps,
  actionButtons,
  reportButtons,
  handleActionOption,
  actionEntities,
  styles,
  noReport,
}: IWorkspaceActionButtonsProps) => {
  const { selectionState, screenReference, entity } = useContext(WorkspaceContext);
  const { views, viewScreenName } = useContext(ViewContext);

  const app = useContext(AppContext);
  const history = useHistory();

  const [genericReportAction, genericReportModal] = useWorkspaceGenericReportAction();

  const [toBeClicked, setToBeClicked] = useState<null | (() => void)>(null);

  const {
    currentUser: { bulkActionWarningThreshold },
  } = useContext(UserContext);

  const confirmBulkActionModal = (
    <ConfirmBulkActionModal
      toBeClicked={toBeClicked}
      setToBeClicked={setToBeClicked}
      numItemsEffected={selectionState.count}
    />
  );

  const _actionButtons = (entity && entity.actions && [...entity.actions]) || [];
  if (!!actionButtons && actionButtons.length > 0) {
    actionButtons.forEach((action) => _actionButtons.push(action));
  }

  const renderedActionButtonMap: {
    [key in WorkspaceActionHeader]?: Array<
      | IContextualMenuItem
      | {
          key: string;
          element: ReactNode | ReactElement;
        }
    >;
  } = {};
  workspaceActionHeaderForEach((header) => (renderedActionButtonMap[header] = []));

  const individualButtons: Array<ReactNode> = [];

  _actionButtons
    .filter((actionButton) => {
      return !(
        !checkAction({ screenReference, entity, app }, actionButton) ||
        !(isDeprecatedWorkspaceActionType(actionButton) || isWorkspaceActionType(actionButton))
      );
    })
    .filter(
      (actionButton: IAbstractWorkspaceAction) =>
        !actionButton.condition || actionButton.condition(actionEntities, { todo: true }, 'WORKSPACE') //TODO - Figure out if any conditions rely on 'props' as a second argument and update them to hooks actions to resolve.
    )
    .forEach((actionButton, index) => {
      if (isWorkspaceActionType(actionButton) && actionButton.key && actionButton.text) {
        const interceptOnClick = (e: any) => {
          if (
            !actionButton?.ignoreBulkActionCheck &&
            (selectionState.count >= bulkActionWarningThreshold || selectionState.count === 0)
          ) {
            setToBeClicked(() => actionButton.onClick);
          } else if (typeof actionButton.onClick === 'function') {
            actionButton.onClick(e);
          }
        };

        if (actionButton.individualButton) {
          individualButtons.push(
            <ActionButton key={actionButton.key} buttonProps={actionButton.buttonProps} onClick={interceptOnClick}>
              {actionButton.text}
            </ActionButton>
          );
        } else {
          const actionButtonWithConfirmation = {
            ...actionButton,
            onClick: interceptOnClick,
          };

          renderedActionButtonMap[actionButton.header || WorkspaceActionHeader.GENERAL]?.push(
            actionButtonWithConfirmation as IContextualMenuItem
          );
        }
      } else if (isDeprecatedWorkspaceActionType(actionButton) && actionButton.element) {
        renderedActionButtonMap[actionButton.header || WorkspaceActionHeader.GENERAL]?.push({
          key: `workspaceActionButton${index}`,
          element: (
            <Suspense
              key={actionButton.key ?? actionButton.actionReference ?? actionButton.description}
              fallback={<ProgressIndicator />}
            >
              {actionButton.element}
            </Suspense>
          ),
        });
      } else if (isDeprecatedWorkspaceActionType(actionButton)) {
        const actionProps = {
          workspaceProps: workspaceProps,
          selectionState: selectionState,
          index: index,

          history: history,
          ...actionButton,
        };

        const actionClick = () => {
          handleActionOption(actionProps);
        };

        const actionClickDivert = (_: any, callback: () => void) => {
          const executeActions = () => {
            actionClick();
            if (typeof callback === 'function') {
              callback();
            }
          };

          if (
            !actionButton?.ignoreBulkActionCheck &&
            (selectionState.count >= bulkActionWarningThreshold || selectionState.count === 0)
          ) {
            setToBeClicked(() => executeActions);
          } else {
            executeActions();
          }
        };

        renderedActionButtonMap[actionButton.header || WorkspaceActionHeader.GENERAL]?.push({
          key: `workspaceActionButton${index}`,
          header: actionButton.header || WorkspaceActionHeader.GENERAL,
          element: (
            <WorkspaceActionButton
              key={`workspaceActionButton${
                actionButton.key ?? actionButton.actionReference ?? actionButton.description
              }`}
              handleClick={actionClickDivert}
              onTrigger={actionClickDivert}
              {...(actionProps as any)}
            />
          ),
        });
      }
    });

  const genericReportActionWithConfirmation = {
    ...genericReportAction,
    onClick: (e: any) => {
      if (selectionState.count >= bulkActionWarningThreshold || selectionState.count === 0) {
        setToBeClicked(() => genericReportAction.onClick);
      } else if (typeof genericReportAction.onClick === 'function') {
        genericReportAction.onClick(e);
      }
    },
  };

  const includeGenericReportAction = views?.[viewScreenName]?.currentView && !noReport;

  if (includeGenericReportAction || reportButtons) {
    //Probably cleaner than tagging a 'report: true' prop onto the buttons, albeit possibly more changes required to implement across the system.
    if (reportButtons) {
      reportButtons.forEach((reportButton) => {
        const reportButtonWithConfirmation = {
          ...reportButton,
          onClick: (e: any) => {
            if (selectionState.count >= bulkActionWarningThreshold || selectionState.count === 0) {
              setToBeClicked(() => reportButton.onClick);
            } else if (typeof reportButton.onClick === 'function') {
              reportButton.onClick(e);
            }
          },
        };
        renderedActionButtonMap[WorkspaceActionHeader.REPORTS]?.push(reportButtonWithConfirmation);
      });
    }
    if (includeGenericReportAction) {
      renderedActionButtonMap[WorkspaceActionHeader.REPORTS]?.push(genericReportActionWithConfirmation);
    }
  }

  const renderedButtons: Array<IContextualMenuItem | { key: string; element: ReactNode | ReactElement }> = [];
  workspaceActionHeaderForEach((header: WorkspaceActionHeader) => {
    if ((renderedActionButtonMap[header]?.length ?? 0) > 0) {
      renderedButtons.push({
        key: `actionHeader${header}`,
        itemType: ContextualMenuItemType.Header,
        text: header,
      });
      renderedButtons.push(...(renderedActionButtonMap[header] || []));
    }
  });

  if (renderedButtons.length === 0) {
    renderedButtons.push({
      key: 'noActions',
      element: (
        <div className={styles?.btnDropdownOption} key="actionButton">
          No actions available
        </div>
      ),
    });
  }

  return (
    <>
      {genericReportModal}
      {confirmBulkActionModal}
      <ActionButtonContext.Provider
        value={{
          individualButtons: true,
        }}
      >
        {individualButtons}
      </ActionButtonContext.Provider>
      <ActionButtonContext.Provider
        value={{
          individualButtons: false,
        }}
      >
        <Button
          text="Actions"
          menuAs={HiddenVerticalScrollMenu}
          menuProps={{
            contextualMenuItemAs: ItemRenderer,
            items: renderedButtons,
          }}
          data-testid="formContextButton_"
          persistMenu
          theme={InfoTheme}
          tabIndex={1}
        />
      </ActionButtonContext.Provider>
    </>
  );
};

//Note - Incomplete - Do not 100% trust these types - They're set to a best-guess for the moment.
type IWorkspaceActionButtonProps = {
  selectionState: IWorkspaceSelectionState;
  workspaceProps: any;
  mutationWrapper?: IDeprecatedWorkspaceAction['mutationWrapper'];
  index: number;
  history: any;
  disabled: boolean;
  handleClick: (_: any, callback?: () => void) => void;
  onTrigger: (_: any, callback?: () => void) => void;
  description?: string;
  mutation?: DocumentNode;
  call?: (props: any) => any;
  dispatch?: Dispatch;
  url?: string;
};

const WorkspaceActionButton = (props: IWorkspaceActionButtonProps) => {
  const { selectionState, workspaceProps, mutationWrapper, index, history, disabled = false, handleClick } = props;
  const dispatch = useDispatch();
  const testid = `workspaceAction_${(props.description || ' undefined').replace(' ', '_')}`;

  if (mutationWrapper && props.mutation && props.call) {
    const onCompleted = (data: any) => {
      if (mutationWrapper.onCompleteMutation) {
        mutationWrapper.onCompleteMutation(data, workspaceProps);
        return;
      }

      dispatch(
        ServiceActions.genericAlert(mutationWrapper.subjectSuccess, mutationWrapper.messageSuccess, AlertType.Info)
      );
      history.go(0);
    };
    const onError =
      mutationWrapper.onError || ((data: any) => handleComponentQueryError(data, { ...(props as any), dispatch }));

    return (
      <Mutation key={testid} mutation={props.mutation} onCompleted={onCompleted} onError={onError}>
        {(callMutation: any) => {
          return (
            <ActionButton
              index={index}
              key={`actionButton${index}`}
              testId={testid}
              actionButton={props}
              onClick={() => {
                callMutation(props.call?.({ ...workspaceProps, selectedAll: selectionState.selectedAll }));
              }}
              disabled={disabled}
            />
          );
        }}
      </Mutation>
    );
  } else if (props.call || props.dispatch || props.url) {
    //props.dispatch shouldn't really be an option, but this is for deprecated action types anyway.
    return (
      <ActionButton
        index={index}
        key={`actionButton${index}`}
        testId={testid}
        actionButton={props}
        onClick={handleClick}
        disabled={disabled}
      />
    );
  }

  return null;
};
